import React, { useState, useEffect } from 'react';
import { Button, Dialog } from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import { entityNameMinimumLength, validateEntityName } from 'const/regex';
import { Campaign, Store, User } from '@restore/services-lib';
import MultiselectDropdown from 'components/DropdownSelect/MultiSelectDropdown/MultiselectDropdown';
import { useStoresContext } from 'pages/Stores/stores.context';
import { useUsersContext } from 'context/users.context';
import {
  CampaignInterface,
  UpdateCampaignInterface,
  useCampaignsContext,
} from 'pages/Campaigns/campaigns.context';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import CustomIcon from 'components/Icon/CustomIcon';
import { FormFieldInterface } from 'components/Form/Form.types';
import Form from 'components/Form/Form';
import TagMultiSelectDropdown from 'components/DropdownSelect/MultiSelectDropdown/Usage/TagsMultiSelectDropdown';
import { areDatesInvalid } from 'utils/dateValidation';
import { getUsersName } from 'utils/formatting';
import styled from 'styled-components';
import { themeOptions } from 'theme';
import { useAuthContext } from 'context/auth.context';
import { hasPermissions } from 'config/ability';

export const ErrorTextWrapper = styled.div`
  margin: 0 0 1rem 1rem;
  color: ${themeOptions.colors.error900};
`;

const getUpdatedCampaignInfo = (campaign: CampaignInterface) => ({
  id: campaign.id,
  name: campaign?.name ?? '',
  storeIds: campaign?.stores?.map((store) => store.id) ?? [],
  tagIds: campaign?.tags?.map((tag) => tag.id) ?? [],
  userIds: campaign?.users?.map((user) => user.id) ?? [],
  startDate: campaign?.startDate,
  endDate: campaign?.endDate,
});

const getSelectedOptionsStores = (campaign: CampaignInterface) =>
  campaign.stores.map((store: Store) => ({ id: store.id, name: store.name }));

const getSelectedOptionsUsers = (campaign: CampaignInterface) =>
  campaign.users?.map((user: User) => ({
    id: user.id,
    name: getUsersName(user),
  }));

const UpdateCampaign = ({ campaign }: { campaign: CampaignInterface }) => {
  const [show, setShow] = useState<boolean>(false);
  const { editCampaign, updateCampaignLoading } = useCampaignsContext();
  const { stores } = useStoresContext();
  const { users } = useUsersContext();
  const { ability, userTenantId } = useAuthContext();
  const [selectedOptionsStores, setSelectedOptionsStores] = useState<DropdownOption[]>(
    getSelectedOptionsStores(campaign)
  );
  const [selectedOptionsUsers, setSelectedOptionsUsers] = useState<DropdownOption[]>(
    getSelectedOptionsUsers(campaign)
  );
  const [updatedCampaign, setUpdatedCampaign] = useState<UpdateCampaignInterface>(
    getUpdatedCampaignInfo(campaign)
  );

  const storesOptions: DropdownOption[] = stores.map((store) => ({
    name: store.name,
    id: store.id,
  }));

  const usersOptions: DropdownOption[] = users.map((user) => ({
    name: getUsersName(user),
    id: user.id,
  }));

  useEffect(() => {
    setUpdatedCampaign(getUpdatedCampaignInfo(campaign));
    setSelectedOptionsStores(getSelectedOptionsStores(campaign));
    setSelectedOptionsUsers(getSelectedOptionsUsers(campaign));
  }, [campaign]);

  // Return null if user does not have 'Update' permissions
  if (
    !hasPermissions(ability, 'update', 'Campaign', { tenantId: userTenantId }) &&
    !hasPermissions(ability, 'update', 'Campaign', { campaignId: campaign.id })
  ) {
    return null;
  }

  const handleUpdateCampaign = async () => {
    if (!updatedCampaign.startDate) {
      await editCampaign({
        ...updatedCampaign,
        // @ts-ignore
        startDate: new Date().toISOString().slice(0, 10) as Date,
      });
    } else {
      await editCampaign(updatedCampaign);
    }

    setUpdatedCampaign(getUpdatedCampaignInfo(campaign));
    setShow(false);
  };

  const handleUpdateCampaignStores = (updatedStores: DropdownOption[]) => {
    setSelectedOptionsStores(updatedStores);
    setUpdatedCampaign({
      ...updatedCampaign,
      storeIds: updatedStores.map((store) => Number(store.id)),
    });
  };

  const handleUpdateTags = (updateTags: DropdownOption[]) => {
    setUpdatedCampaign({
      ...updatedCampaign,
      tagIds: updateTags.map((tag) => Number(tag.id)),
    });
  };

  const handleUpdateCampaignUsers = (updatedUsers: DropdownOption[]) => {
    setSelectedOptionsUsers(updatedUsers);
    setUpdatedCampaign({
      ...updatedCampaign,
      userIds: updatedUsers.map((user) => Number(user.id)),
    });
  };

  const handleChangeField = (value: string | number, key: keyof Campaign) => {
    setUpdatedCampaign({ ...updatedCampaign, [key]: value });
  };

  const fields: FormFieldInterface[] = [
    {
      label: 'Campaign Name',
      value: updatedCampaign.name,
      onChange: (name) => setUpdatedCampaign({ ...updatedCampaign, name }),
      fullWidth: true,
      requiredMinimumLength: entityNameMinimumLength,
      validator: validateEntityName,
    },
    {
      label: 'Stores',
      onChange: () => {},
      customField: (
        <MultiselectDropdown
          fieldLabel='Stores'
          allOptions={storesOptions}
          fullWidth={true}
          selected={selectedOptionsStores}
          setSelected={(selectedOptions: DropdownOption[]) =>
            handleUpdateCampaignStores(selectedOptions)
          }
        />
      ),
    },
    {
      label: 'Users',
      onChange: () => {},
      customField: (
        <MultiselectDropdown
          fieldLabel='Users'
          allOptions={usersOptions}
          fullWidth={true}
          selected={selectedOptionsUsers}
          setSelected={(selectedOptions: DropdownOption[]) =>
            handleUpdateCampaignUsers(selectedOptions)
          }
        />
      ),
    },
    {
      label: 'Tags',
      onChange: () => {},
      customField: (
        <TagMultiSelectDropdown selectedTags={campaign.tags} onChange={handleUpdateTags} />
      ),
    },
    {
      label: 'Start Date',
      type: 'date',
      md: 6,
      // @ts-ignore
      value: updatedCampaign.startDate,
      onChange: (startDate) => handleChangeField(startDate, 'startDate'),
    },
    {
      label: 'End Date',
      type: 'date',
      md: 6,
      // @ts-ignore
      value: updatedCampaign.endDate,
      onChange: (endDate) => handleChangeField(endDate, 'endDate'),
    },
  ];

  return (
    <>
      <CustomIcon onClick={() => setShow(true)} icon={<Edit />} />
      <Dialog open={show} onClose={() => setShow(false)}>
        <Form
          title='Update Campaign'
          fields={fields}
          onSubmit={handleUpdateCampaign}
          submitting={updateCampaignLoading}
          buttonText='Update Campaign'
          disabled={areDatesInvalid(updatedCampaign.startDate, updatedCampaign.endDate)}
        />
        <ErrorTextWrapper>
          {areDatesInvalid(updatedCampaign.startDate, updatedCampaign.endDate) &&
            '* End Date cannot be before Start Date'}
        </ErrorTextWrapper>
      </Dialog>
    </>
  );
};

export default UpdateCampaign;
